import * as React from 'react';
import { useRef, useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '../ui';
import { getDateLocale } from '../../lib/date-locale';
import type { PopoverSide } from '../../lib/popover-side';
import { computePopoverSide } from '../../lib/popover-side';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DatePickerProps {
  className?: string;
  disabled?: boolean;
  onChange: (date: Date | null) => void;
  placeholder: string;
  value: Date | null | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DatePicker({
  className,
  disabled = false,
  onChange,
  placeholder,
  value,
}: DatePickerProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<PopoverSide>('bottom');

  function handleOpenChange(nextOpen: boolean): void {
    if (disabled) return;
    if (nextOpen) {
      setSide(computePopoverSide(triggerRef.current));
    }
    setOpen(nextOpen);
  }

  function handleSelect(date: Date | undefined): void {
    onChange(date ?? null);
    setOpen(false);
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          className={cn(
            'h-8 justify-start gap-1.5 px-2 text-xs font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
          disabled={disabled}
          type="button"
          variant="outline"
        >
          <CalendarIcon className="h-4 w-4" />
          {value ? format(value, 'dd/MM/yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[60] w-auto p-0" side={side}>
        <Calendar
          className="min-w-[280px]"
          autoFocus
          defaultMonth={value ?? undefined}
          locale={locale}
          mode="single"
          onSelect={handleSelect}
          selected={value ?? undefined}
        />
      </PopoverContent>
    </Popover>
  );
}
